/**
 * A grid of swatch circles: seven hues across, pastel / normal / dark down. The grid is one
 * radio group with a roving tab stop, so Tab enters it once and the arrow keys move in two
 * dimensions. The shadcn registry has no colour picker. It sits inside the period's own
 * floating card, so it draws the grid inline rather than opening a second popover over it.
 */
import { Check } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { Button } from '../../../../ui/button';
import { profileCatalogue } from './messages';
import {
  PERIOD_HUES,
  PERIOD_SHADES,
  swatchVars,
  type PeriodHue,
  type PeriodShade,
  type SwatchKey,
} from './periods';

const t = profileCatalogue.t;
const COLS = PERIOD_HUES.length;
const KEYS: SwatchKey[] = PERIOD_SHADES.flatMap((shade) =>
  PERIOD_HUES.map((hue): SwatchKey => `${hue}.${shade}`),
);

function swatchName(key: SwatchKey): string {
  const [hue, shade] = key.split('.') as [PeriodHue, PeriodShade];
  return t('color.swatch', { hue: t(`color.hue.${hue}`), shade: t(`color.shade.${shade}`) });
}

export function PeriodColorPicker(props: {
  /** 1-based, for the accessible names. */
  n: number;
  /** The stored key; `null` follows the frequency. */
  value: SwatchKey | null;
  /** What `null` resolves to now, marked as the chosen swatch while nothing is stored. */
  resolved: SwatchKey;
  onChange: (value: SwatchKey | null) => void;
  disabled?: boolean;
}): ReactNode {
  const selected = props.value ?? props.resolved;
  const [focusIndex, setFocusIndex] = useState(() => Math.max(0, KEYS.indexOf(selected)));
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  function move(event: KeyboardEvent<HTMLDivElement>): void {
    const deltas: Record<string, number> = {
      ArrowRight: 1,
      ArrowLeft: -1,
      ArrowDown: COLS,
      ArrowUp: -COLS,
    };
    let next: number | undefined;
    if (event.key in deltas) next = (focusIndex + deltas[event.key]! + KEYS.length) % KEYS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = KEYS.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setFocusIndex(next);
    buttons.current[next]?.focus();
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        tabIndex={-1}
        aria-label={t('color.label', { n: props.n })}
        className="grid grid-cols-7 gap-2"
        onKeyDown={move}
      >
        {KEYS.map((key, index) => {
          const checked = key === selected;
          const vars = swatchVars(key);
          return (
            <button
              key={key}
              ref={(node) => {
                buttons.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={swatchName(key)}
              disabled={props.disabled}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => {
                setFocusIndex(index);
                props.onChange(key);
              }}
              style={{ background: vars.background, color: vars.color }}
              className="flex size-7 items-center justify-center rounded-full border border-border outline-none hover:scale-110 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover motion-safe:transition-transform"
            >
              {checked && <Check aria-hidden className="size-4" />}
            </button>
          );
        })}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-full"
        disabled={props.disabled}
        aria-pressed={props.value === null}
        onClick={() => props.onChange(null)}
      >
        {t('color.auto')}
      </Button>
    </div>
  );
}
